```vue
<template>
  <div class="dropdown" v-if="options">
    <!-- Dropdown Input -->
    <input
      class="dropdown-input"
      :name="name"
      @focus="showOptions"
      @blur="exit"
      @keyup="keyMonitor"
      @input="onInput"
      v-model="searchFilter"
      :disabled="disabled"
      :placeholder="placeholder"
    />

    <!-- Dropdown Menu -->
    <div class="dropdown-content" v-show="optionsShown">
      <div
        class="dropdown-item"
        v-for="(option, index) in filteredOptions"
        :key="index"
        @mousedown="selectOption(option)"
      >
        {{ option.name || option.id || "-" }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Dropdown",
  props: {
    name: {
      type: String,
      default: "dropdown",
    },
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: "Please select an option",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    maxItem: {
      type: Number,
      default: 30,
    },
  },

  data() {
    return {
      selected: {},
      optionsShown: false,
      searchFilter: "",
    };
  },

  created() {
    this.$emit("selected", this.selected);
  },

  computed: {
    filteredOptions() {
      if (!this.searchFilter) {
        return [];
      }

      const regOption = new RegExp(this.searchFilter, "i");

      return this.options
        .filter(option => option.name && regOption.test(option.name))
        .slice(0, this.maxItem);
    },
  },

  methods: {
    onInput() {
      // Only emit when the user actually types.
      this.$emit("filter", this.searchFilter);

      // Keep the highlighted selection in sync while typing.
      if (this.filteredOptions.length > 0) {
        this.selected = this.filteredOptions[0];
      } else {
        this.selected = {};
      }

      this.optionsShown = true;
    },

    selectOption(option) {
      this.selected = option;
      this.optionsShown = false;

      // This updates the displayed text, but does NOT fire @input.
      this.searchFilter = option.name;

      this.$emit("selected", option);
    },

    showOptions() {
      if (!this.disabled) {
        this.searchFilter = "";
        this.optionsShown = true;
      }
    },

    exit() {
      if (!this.selected.id) {
        this.selected = {};
        this.searchFilter = "";
      } else {
        this.searchFilter = this.selected.name;
      }

      this.$emit("selected", this.selected);
      this.optionsShown = false;
    },

    keyMonitor(event) {
      if (event.key === "Enter" && this.filteredOptions[0]) {
        this.selectOption(this.filteredOptions[0]);
      }
    },
  },
};
</script>

<style scoped>
.dropdown-input {
  display: block;
  width: 50%;
  height: calc(1.5em + 0.75rem + 2px);
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: var(--text-secondary);
  background-color: var(--bg-input);
  background-clip: padding-box;
  border: 1px solid var(--border-input);
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.dropdown-content {
  position: absolute;
  background-color: var(--bg-surface);
  min-width: 248px;
  max-width: 248px;
  max-height: 248px;
  border: 1px solid var(--border-default);
  box-shadow: 0px -8px 34px 0px var(--shadow-sm);
  overflow: auto;
  z-index: 1;
}

.dropdown-item {
  color: var(--text-primary);
  font-size: 0.7em;
  line-height: 1em;
  padding: 8px;
  text-decoration: none;
  display: block;
  cursor: pointer;
}

.dropdown-item:hover {
  background-color: var(--bg-hover);
}
</style>
```
